import React, { useEffect, useState } from 'react';
import { Modal, Spin, Alert, List, Tag, Typography, Button } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';

import { avaliacoesAdminService } from '../../services/api';
import { buildApiErrorMessage } from '../../utils/apiError';

const { Text } = Typography;

// Contrato real: GET /api/avaliacoes/periodos/{id}/turmas/{turmaId}/alunos
// (DetalheAvaliacaoTurmaResponse) -- somente consulta, o Admin NUNCA edita
// avaliação de aluno aqui (nem chama endpoints /api/professor/...).
export const STATUS_ALUNO_INFO = {
  PENDENTE: { label: 'Não iniciada', color: 'default' },
  RASCUNHO: { label: 'Em andamento', color: 'gold' },
  AGUARDANDO_VALIDACAO: { label: 'Aguardando validação', color: 'blue' },
  PUBLICADA: { label: 'Publicada', color: 'green' },
};

// InconsistenciaElegibilidadeResponse não devolve nome do aluno (só
// id_matricula) -- honesto com o contrato real, nunca inventa um cross-
// reference com `alunos` (matrículas inconsistentes são, por definição,
// as que NÃO entraram nessa lista). Único código existente hoje
// (avaliacao_elegibilidade_service.CODIGO_VIGENCIAS_SOBREPOSTAS), mas o
// fallback cobre qualquer código futuro sem nunca expor termos técnicos.
const mensagemInconsistencia = () =>
  'Esta matrícula não foi incluída na avaliação porque o histórico da turma precisa ser revisado.';

const DetalheTurmaAvaliacaoModal = ({ open, periodoId, turmaId, onClose }) => {
  const [detalhe, setDetalhe] = useState(null);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState(null);

  const carregar = async () => {
    if (!periodoId || !turmaId) return;
    setLoading(true);
    setErro(null);
    try {
      const response = await avaliacoesAdminService.obterAlunosTurma(periodoId, turmaId);
      setDetalhe(response.data);
    } catch (err) {
      const { message: mensagem } = buildApiErrorMessage(
        err,
        'Período ou turma não encontrado.'
      );
      setErro(mensagem);
      setDetalhe(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      carregar();
    } else {
      setDetalhe(null);
      setErro(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, periodoId, turmaId]);

  return (
    <Modal
      title={detalhe ? `${detalhe.nome_turma} — ${detalhe.nome_equipe}` : 'Detalhe da turma'}
      open={open}
      onCancel={onClose}
      footer={
        <Button onClick={onClose}>Fechar</Button>
      }
      width={560}
    >
      {loading && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
          <Spin />
        </div>
      )}

      {!loading && erro && (
        <Alert
          type="error"
          showIcon
          message="Não foi possível carregar"
          description={erro}
          action={
            <Button size="small" icon={<ReloadOutlined />} onClick={carregar}>
              Tentar novamente
            </Button>
          }
        />
      )}

      {!loading && !erro && detalhe && (
        <>
          {detalhe.inconsistencias.length > 0 && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message={`${detalhe.inconsistencias.length} matrícula${detalhe.inconsistencias.length > 1 ? 's' : ''} com inconsistência`}
              description={
                <List
                  size="small"
                  dataSource={detalhe.inconsistencias}
                  renderItem={(item) => (
                    <List.Item>
                      <Text>
                        Matrícula #{item.id_matricula} — {mensagemInconsistencia()}
                      </Text>
                    </List.Item>
                  )}
                />
              }
            />
          )}

          <List
            size="small"
            dataSource={detalhe.alunos}
            locale={{ emptyText: 'Nenhum aluno incluído nesta turma.' }}
            renderItem={(aluno) => {
              const statusInfo = STATUS_ALUNO_INFO[aluno.status] || { label: aluno.status, color: 'default' };
              return (
                <List.Item>
                  <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                    <Text>{aluno.nome_aluno}</Text>
                    <Tag color={statusInfo.color}>{statusInfo.label}</Tag>
                  </div>
                </List.Item>
              );
            }}
          />
        </>
      )}
    </Modal>
  );
};

export default DetalheTurmaAvaliacaoModal;
